import { useCallback, useEffect, useState } from "react";
import {
  EMPTY_PREFS,
  HomePrefs,
  WidgetFilterValues,
  clearPrefsCookie,
  readPrefsCookie,
  writePrefsCookie,
} from "./homePrefs";

/** Cookie-backed home page preferences for one user. */
export const useHomePreferences = (uid: string | null) => {
  const [prefs, setPrefs] = useState<HomePrefs>(() => (uid ? readPrefsCookie(uid) : EMPTY_PREFS));

  useEffect(() => {
    setPrefs(uid ? readPrefsCookie(uid) : EMPTY_PREFS);
  }, [uid]);

  const update = useCallback(
    (next: (current: HomePrefs) => HomePrefs) => {
      setPrefs((current) => {
        const value = next(current);
        if (uid) writePrefsCookie(uid, value);
        return value;
      });
    },
    [uid]
  );

  const setOrder = useCallback(
    (order: string[]) => update((p) => ({ ...p, order })),
    [update]
  );

  const setHidden = useCallback(
    (id: string, hidden: boolean) =>
      update((p) => ({
        ...p,
        hidden: hidden
          ? Array.from(new Set([...p.hidden, id]))
          : p.hidden.filter((h) => h !== id),
      })),
    [update]
  );

  const setWidgetFilters = useCallback(
    (id: string, filters: WidgetFilterValues | null) =>
      update((p) => {
        const nextFilters = { ...p.filters };
        if (filters) nextFilters[id] = filters;
        else delete nextFilters[id];
        return { ...p, filters: nextFilters };
      }),
    [update]
  );

  const resetAll = useCallback(() => {
    if (uid) clearPrefsCookie(uid);
    setPrefs(EMPTY_PREFS);
  }, [uid]);

  return { prefs, setOrder, setHidden, setWidgetFilters, resetAll };
};
